import { Popup, MapRef } from 'react-map-gl/mapbox'
import { DayViewEvent, Visit, CalendarEventVisit, VisitWorkareaStatusRecord } from "../scheduleApi"
import dayjs, { Dayjs } from "dayjs"
import { getVisitForEvent, getInitials, getDayDateRange } from "../scheduleModel"
import { isMultiDayEvent } from "./ReorderView/reorderDayViewModel"
import { VisitWorkareaStatuses } from "../../../common/constants"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrafficCone, faBan, faCalendarDay } from '@fortawesome/pro-solid-svg-icons'
import { CheckCircleOutlined, UserAddOutlined } from "@ant-design/icons"
import { Avatar, Tag, Tooltip } from "antd"
import { MarkerStatus } from "../Map/ScheduleMapMarker"
import { RefObject } from "react"

type Props = {
  event: DayViewEvent
  latitude: number
  longitude: number
  visits: Visit[]
  calendarEventVisits: CalendarEventVisit[]
  workareaStatuses: VisitWorkareaStatusRecord[]
  date: Dayjs
  markerStatus: MarkerStatus
  crewLeadAvatar?: string | null
  crewLeadName?: string | null
  crewLeadColor?: string | null
  mapRef: RefObject<MapRef | null>
  onClose: () => void
}

type WorkAreaWithStatus = {
  id: number
  name: string
  status: number | null
}

const DayViewMarkerPopup = ({ event, latitude, longitude, visits, calendarEventVisits, workareaStatuses, date, crewLeadAvatar, crewLeadName, crewLeadColor, mapRef, onClose }: Props) => {
  const visit = getVisitForEvent(event, calendarEventVisits, visits, getDayDateRange(date))

  const getPositioning = () => {
    if (!mapRef.current) {
      return { anchor: 'bottom' as const, offset: { bottom: [0, -95] as [number, number] } }
    }

    const point = mapRef.current.project([longitude, latitude])
    const container = mapRef.current.getContainer()
    const mapWidth = container.clientWidth

    const popupWidth = 320
    const popupHeight = 200
    const markerHeight = 70
    const isNearLeft = point.x < popupWidth
    const isNearRight = point.x > mapWidth - popupWidth
    const isNearTop = point.y < popupHeight + markerHeight

    if (isNearTop) {
      if (isNearLeft) {
        return { anchor: 'top-left' as const, offset: { 'top-left': [15, 10] as [number, number] } }
      }
      if (isNearRight) {
        return { anchor: 'top-right' as const, offset: { 'top-right': [-15, 10] as [number, number] } }
      }
      return { anchor: 'top' as const, offset: { top: [0, 10] as [number, number] } }
    }

    if (isNearLeft) {
      return { anchor: 'left' as const, offset: { left: [15, -30] as [number, number] } }
    }
    if (isNearRight) {
      return { anchor: 'right' as const, offset: { right: [-15, -30] as [number, number] } }
    }

    return { anchor: 'bottom' as const, offset: { bottom: [0, -95] as [number, number] } }
  }

  const { anchor, offset } = getPositioning()

  const address = [
    event.customerAddress1,
    event.customerCity,
    event.customerState,
    event.customerZip
  ].filter(Boolean).join(', ')

  const getWorkAreasWithStatus = (): WorkAreaWithStatus[] => {
    const eventWorkareaIds = (event.workAreaIds && event.workAreaIds !== '')
      ? event.workAreaIds.split(',').map(id => parseInt(id.trim()))
      : []

    const workareaNames = (event.workAreaNames && event.workAreaNames !== '')
      ? event.workAreaNames.split(',')
      : []

    return eventWorkareaIds.map((id, index) => {
      const workareaStatus = visit
        ? workareaStatuses.find(ws => ws.visitId === visit.id && ws.workareaId === id)
        : null

      const nameFromList = workareaNames[index]?.trim()
      const name = (nameFromList && nameFromList !== '')
        ? nameFromList
        : workareaStatus?.workareaName
        ?? `Work Area ${id}`

      return {
        id,
        name,
        status: workareaStatus?.status ?? null
      }
    })
  }

  const workAreas = getWorkAreasWithStatus()

  const renderStatusTag = (status: number | null) => {
    if (status === VisitWorkareaStatuses.InProgress) {
      return (
        <Tag
          style={{
            backgroundColor: '#fff7e6',
            borderColor: '#ffd591',
            color: '#fa8c16',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            margin: 0
          }}
        >
          <FontAwesomeIcon icon={faTrafficCone} style={{ fontSize: '10px' }} />
          In Progress
        </Tag>
      )
    }

    if (status === VisitWorkareaStatuses.Completed || status === VisitWorkareaStatuses.CompletedUnbillable) {
      return (
        <Tag
          style={{
            backgroundColor: '#f6ffed',
            borderColor: '#b7eb8f',
            color: '#52c41a',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            margin: 0
          }}
        >
          <CheckCircleOutlined style={{ fontSize: '10px' }} />
          {status === VisitWorkareaStatuses.CompletedUnbillable ? 'Completed (Unbillable)' : 'Completed'}
        </Tag>
      )
    }

    if (status === VisitWorkareaStatuses.Skipped) {
      return (
        <Tag
          style={{
            backgroundColor: '#f5f5f5',
            borderColor: '#d9d9d9',
            color: '#000',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            margin: 0
          }}
        >
          <FontAwesomeIcon icon={faBan} style={{ fontSize: '10px' }} />
          Skipped
        </Tag>
      )
    }

    if (status === VisitWorkareaStatuses.SkippedReschedule) {
      return (
        <Tag
          style={{
            backgroundColor: '#f5f5f5',
            borderColor: '#d9d9d9',
            color: '#000',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            margin: 0
          }}
        >
          <FontAwesomeIcon icon={faCalendarDay} style={{ fontSize: '10px' }} />
          Skipped (Reschedule)
        </Tag>
      )
    }

    return null
  }

  return (
    <Popup
      latitude={latitude}
      longitude={longitude}
      anchor={anchor}
      onClose={onClose}
      closeButton={false}
      closeOnClick={true}
      offset={offset}
      maxWidth="none"
      style={{ zIndex: 10 }}
      className="day-view-marker-popup"
    >
      <style>{`.day-view-marker-popup .mapboxgl-popup-tip { display: none; } .day-view-marker-popup .mapboxgl-popup-content { max-width: none; }`}</style>
      <div
        style={{ minWidth: '200px', padding: '4px' }}
      >
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' }}>
          {address || 'No address'}
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '10px',
          paddingBottom: '8px',
          borderBottom: `2px solid ${crewLeadColor || '#ccc'}`
        }}>
          {crewLeadName ? (
            <Avatar
              src={crewLeadAvatar}
              size={24}
              style={{
                backgroundColor: '#f0f0f0',
                color: '#000',
                fontSize: '11px',
                fontWeight: 600
              }}
            >
              {getInitials(crewLeadName)}
            </Avatar>
          ) : (
            <Avatar
              size={24}
              icon={<UserAddOutlined />}
              style={{
                backgroundColor: '#FFF1F0',
                border: '1px dashed #820014',
                color: '#820014',
                fontSize: '11px'
              }}
            />
          )}
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>
            {crewLeadName || 'Unassigned'}
          </span>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#000', fontWeight: 500 }}>
            {event.jobName}
          </div>
          <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
            {event.customerDisplayName}
          </div>
          <div style={{ fontSize: '11px', color: '#999', marginTop: '2px', display: 'flex', gap: '4px' }}>
            {isMultiDayEvent(event) || event.allDay ? (
              <>
                {isMultiDayEvent(event) && (
                  <Tooltip title={event.allDay
                    ? `${dayjs(event.start).format('MMM D, YYYY')} - ${dayjs(event.end).format('MMM D, YYYY')}`
                    : `${dayjs(event.start).format('MMM D, YYYY h:mm A')} - ${dayjs(event.end).format('MMM D, YYYY h:mm A')}`
                  }>
                    <Tag style={{ fontSize: '11px', color: '#000' }}>Multi Day</Tag>
                  </Tooltip>
                )}
                {event.allDay && (
                  <Tooltip title="This event was created with the All Day setting.">
                    <Tag style={{ fontSize: '11px', color: '#000' }}>All Day</Tag>
                  </Tooltip>
                )}
              </>
            ) : (
              `${dayjs(event.start).format('h:mm A')} - ${dayjs(event.end).format('h:mm A')}`
            )}
          </div>
          <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {workAreas.length > 0 ? (
              workAreas.map(wa => (
                <div
                  key={wa.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '11px'
                  }}
                >
                  <span style={{ color: '#666' }}>{wa.name}</span>
                  {renderStatusTag(wa.status)}
                </div>
              ))
            ) : (
              <div style={{ fontSize: '11px', color: '#999' }}>No Assigned Work Areas</div>
            )}
          </div>
        </div>
      </div>
    </Popup>
  )
}

export default DayViewMarkerPopup
